import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import TextInput from "../../../../ui/TextInput/TextInput";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImageProvider } from "../../../../__generated__/gql/graphql";
import styles from "./ChangeImageModal.module.scss";
import { Search } from "../../../../ui/Icon/Icon";
import EmptyState from "../../../../ui/EmptyState/EmptyState";
import Loader from "../../../../ui/Loader/Loader";
import { Freepik, Pexels, Unsplash } from "../../../../ui/LogoIcon/LogoIcon";
import abstract from "./abstract.png";
import shapes from "./shapes.png";
import gradient from "./gradient.png";
import nature from "./nature.png";
import FileUpload from "../../../../ui/FileUpload/FileUpload";
import Button from "../../../../ui/Button/Button";
import classNames from "classnames";

function InfiniteScroll({
  children,
  loading,
  hasMore,
  next,
}: {
  children: React.ReactNode;
  loading: boolean;
  hasMore: boolean;
  next: () => void | Promise<void>;
}) {
  const loaderRef = useRef(null);

  useEffect(() => {
    const loader = loaderRef.current;

    const observer = new IntersectionObserver(async (entries) => {
      const target = entries[0];

      if (target.isIntersecting && !loading && hasMore) {
        await next();
      }
    });

    if (loader) {
      observer.observe(loader);
    }

    return () => {
      if (loader) {
        observer.unobserve(loader);
      }
    };
  }, [hasMore, loading, next]);

  return (
    <div>
      <div>{children}</div>
      <div ref={loaderRef} className={styles.loader}>
        {loading && <Loader />}
      </div>
    </div>
  );
}

function Masonry({
  columns,
  items,
}: {
  columns: number;
  items: React.ReactElement[];
}) {
  return (
    <div className={styles.masonry}>
      {Array.from({ length: columns }).map((_, i) => (
        <div className={styles.masonryColumn} key={i}>
          {items.filter((_, j) => j % columns === i)}
        </div>
      ))}
    </div>
  );
}

const SearchImagesQuery = graphql(/* GraphQL */ `
  query searchImages(
    $input: SearchImagesInput!
    $paginationOptions: PagePaginationOptions!
  ) {
    searchImages(input: $input, paginationOptions: $paginationOptions) {
      totalCount
      nodes {
        id
        url
        alt
        width
        height
        authorName
        authorUrl
      }
    }
  }
`);

const SelectImageMutation = graphql(/* GraphQL */ `
  mutation selectImage($input: SelectImageInput!) {
    selectImage(input: $input) {
      id
      url
      alt
      width
      height
      authorName
      authorUrl
      searchTerm
    }
  }
`);

const UploadImageMutation = graphql(/* GraphQL */ `
  mutation uploadImage($input: UploadImageInput!) {
    uploadImage(input: $input) {
      id
      url
      alt
      width
      height
      authorName
      authorUrl
      searchTerm
    }
  }
`);

const DEFAULT_PAGINATION_STATE = {
  page: 1,
};

function InternalChangeImageModal({
  id,
  onClose,
  defaultSearchTerm,
  defaultProvider = ImageProvider.Unsplash,
  onSubmit,
}: {
  id?: string;
  onClose: () => void;
  defaultSearchTerm?: string;
  defaultProvider?: ImageProvider;
  onSubmit?: (image: { id: string; alt: string }) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [paginationState, setPaginationState] = useState(
    DEFAULT_PAGINATION_STATE,
  );
  const [keyPhrase, setKeyPhrase] = useState<string>(defaultSearchTerm ?? "");
  const [debouncedKeyPhrase, setDebouncedKeyPhrase] = useState<string>(
    defaultSearchTerm ?? "",
  );
  const [provider, setProvider] = useState<ImageProvider>(
    defaultProvider !== ImageProvider.User
      ? defaultProvider
      : ImageProvider.Unsplash,
  );
  const isInitialized = useRef(false);

  const [, selectImage] = useMutation(SelectImageMutation);
  const [, uploadImage] = useMutation(UploadImageMutation);

  const [searchImagesResponse] = useQuery({
    query: SearchImagesQuery,
    variables: {
      paginationOptions: {
        page: paginationState.page,
        pageSize: 10,
      },
      input: {
        provider: provider,
        query: debouncedKeyPhrase,
      },
    },
    pause: !debouncedKeyPhrase,
  });
  const [images, setImages] = useState<
    NonNullable<(typeof searchImagesResponse)["data"]>["searchImages"]["nodes"]
  >([]);

  const onImageClick = useCallback(
    async (imageId: string) => {
      setLoading(true);

      const response = await selectImage({
        input: {
          searchTerm: debouncedKeyPhrase,
          pexelsId: provider === ImageProvider.Pexels ? imageId : undefined,
          unsplashId: provider === ImageProvider.Unsplash ? imageId : undefined,
          freepikId: provider === ImageProvider.Freepik ? imageId : undefined,
        },
      });

      const image = response.data?.selectImage;

      if (image) {
        onSubmit?.({ id: image.id, alt: image.alt ?? "" });
      }

      onClose();
    },
    [setLoading, selectImage, debouncedKeyPhrase, provider, onClose, onSubmit],
  );

  const onImageUpload = useCallback(
    async (uploadedImage: {
      id: string;
      url: string;
      width: number;
      height: number;
    }) => {
      const response = await uploadImage({
        input: {
          id: uploadedImage.id,
          url: uploadedImage.url,
          width: uploadedImage.width,
          height: uploadedImage.height,
        },
      });

      const image = response.data?.uploadImage;

      if (image) {
        onSubmit?.({ id: image.id, alt: image.alt ?? "" });
      }

      onClose();
    },
    [onClose, onSubmit, uploadImage],
  );

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    const timeout = setTimeout(() => {
      setImages([]);
      setPaginationState(DEFAULT_PAGINATION_STATE);
      setDebouncedKeyPhrase(keyPhrase);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [keyPhrase]);

  useEffect(() => {
    setImages((images) => [
      ...images,
      ...(searchImagesResponse.data?.searchImages.nodes ?? []).filter(
        (image) => !images.map((i) => i.id).includes(image.id),
      ),
    ]);
  }, [searchImagesResponse.data]);

  return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.loadingOverlay}>
          <Loader color="white" />
        </div>
      )}
      <div className={styles.leftSection}>
        <Button
          variant="unstyled"
          onClick={() => {
            setImages([]);
            setProvider(ImageProvider.Unsplash);
          }}
          className={classNames(styles.imageProvider, {
            [styles.imageProviderActive]: provider === ImageProvider.Unsplash,
          })}
        >
          <Unsplash className={styles.imageProviderLogo} size="xsmall" />
          <p>Unsplash</p>
        </Button>
        <Button
          variant="unstyled"
          onClick={() => {
            setImages([]);
            setProvider(ImageProvider.Freepik);
          }}
          className={classNames(styles.imageProvider, {
            [styles.imageProviderActive]: provider === ImageProvider.Freepik,
          })}
        >
          <Freepik className={styles.imageProviderLogo} size="xsmall" />
          <p>Freepik</p>
        </Button>
        <Button
          variant="unstyled"
          onClick={() => {
            setImages([]);
            setProvider(ImageProvider.Pexels);
          }}
          className={classNames(styles.imageProvider, {
            [styles.imageProviderActive]: provider === ImageProvider.Pexels,
          })}
        >
          <Pexels className={styles.imageProviderLogo} size="xsmall" />
          <p>Pexels</p>
        </Button>
        <FileUpload className={styles.uploadButton} onUpload={onImageUpload} />
      </div>
      <div className={styles.rightSection}>
        <TextInput
          leftSection={
            searchImagesResponse.fetching ? (
              <Loader size="xsmall" />
            ) : (
              <Search />
            )
          }
          className={styles.input}
          value={keyPhrase}
          onChange={(e) => setKeyPhrase(e.target.value)}
          clearable={!!keyPhrase}
          onClear={() => {
            setKeyPhrase("");
          }}
        />

        <div className={styles.defaults}>
          <button
            type="button"
            className={styles.default}
            onClick={() => setKeyPhrase("Abstract")}
          >
            <img src={abstract} alt="" className={styles.defaultImage} />
            <span>Abstract</span>
          </button>
          <button
            type="button"
            className={styles.default}
            onClick={() => setKeyPhrase("Shapes")}
          >
            <img src={shapes} alt="" className={styles.defaultImage} />
            <span>Shapes</span>
          </button>
          <button
            type="button"
            className={styles.default}
            onClick={() => setKeyPhrase("Nature")}
          >
            <img src={nature} alt="" className={styles.defaultImage} />
            <span>Nature</span>
          </button>
          <button
            type="button"
            className={styles.default}
            onClick={() => setKeyPhrase("Gradient")}
          >
            <img src={gradient} alt="" className={styles.defaultImage} />
            <span>Gradient</span>
          </button>
        </div>

        {!!images?.length && (
          <InfiniteScroll
            loading={searchImagesResponse.fetching}
            hasMore={
              !!searchImagesResponse.data?.searchImages.totalCount &&
              images.length < searchImagesResponse.data?.searchImages.totalCount
            }
            next={() => {
              setPaginationState((state) => ({
                ...state,
                page: state.page + 1,
              }));
            }}
          >
            <Masonry
              columns={2}
              items={
                images?.map((image) => (
                  <div key={image.id}>
                    <button
                      type="button"
                      className={styles.imageButton}
                      onClick={() => onImageClick(image.id)}
                    >
                      <img
                        width={image.width}
                        height={image.height}
                        className={styles.image}
                        src={image.url}
                        alt={image.alt ?? ""}
                      />
                    </button>
                    {image.authorUrl && image.authorName && (
                      <p className={styles.imageCredit}>
                        By{" "}
                        <a
                          href={`${image.authorUrl}?utm_source=macaw&utm_medium=referral`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {image.authorName}
                        </a>
                      </p>
                    )}
                  </div>
                )) ?? []
              }
            />
          </InfiniteScroll>
        )}

        {!searchImagesResponse.fetching && !images?.length && (
          <EmptyState
            icon={<Search />}
            hideBackground
            title={
              debouncedKeyPhrase
                ? "No results found"
                : "Enter your search term to find images"
            }
          />
        )}
      </div>
    </div>
  );
}

function ChangeImageModal({
  id,
  defaultSearchTerm,
  defaultProvider,
  isOpen,
  onClose,
  onSubmit,
}: {
  id?: string;
  defaultSearchTerm?: string;
  defaultProvider?: ImageProvider;
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (image: { id: string; alt: string }) => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      classNames={{ body: styles.modalBody, container: styles.modalContainer }}
      hideHeader
      fullHeight
    >
      <InternalChangeImageModal
        onClose={onClose}
        id={id}
        defaultProvider={defaultProvider}
        defaultSearchTerm={defaultSearchTerm}
        onSubmit={onSubmit}
      />
    </Modal>
  );
}

export default ChangeImageModal;
